/**
 * Wordle Game Engine
 *
 * Core game logic for the Daily Word (Wordle-style) game.
 * Handles word evaluation, duplicate letter logic, hard mode validation,
 * daily word selection (deterministic based on date), and practice mode.
 */

import {
  LetterState,
  LetterResult,
  GuessResult,
  WordleGameState,
  WordleGameStatus,
  WordleGameMode,
  KeyboardState,
  MAX_GUESSES,
  WORD_LENGTH,
} from './types';
import { SOLUTION_WORDS, isValidWord } from './wordList';

/** Epoch date for daily puzzle numbering (Jan 1, 2024) */
const EPOCH_DATE = new Date(2024, 0, 1).getTime();
const MS_PER_DAY = 86400000;

export class WordleEngine {
  /**
   * Create a new game state
   */
  createGame(mode: WordleGameMode, hardMode: boolean = false): WordleGameState {
    const targetWord = mode === 'daily'
      ? this.getDailyWord()
      : this.getRandomWord();

    return {
      targetWord,
      guesses: [],
      currentGuess: '',
      currentRow: 0,
      gameStatus: 'playing',
      mode,
      hardMode,
      keyboardState: {},
      gameId: this.generateGameId(),
      startedAt: Date.now(),
      timer: 0,
      puzzleNumber: mode === 'daily' ? this.getDailyPuzzleNumber() : undefined,
    };
  }

  /**
   * Get the daily word (deterministic based on date)
   */
  getDailyWord(): string {
    const puzzleNumber = this.getDailyPuzzleNumber();
    const index = puzzleNumber % SOLUTION_WORDS.length;
    return SOLUTION_WORDS[index];
  }

  /**
   * Get the daily puzzle number (days since epoch)
   */
  getDailyPuzzleNumber(): number {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    return Math.floor((today - EPOCH_DATE) / MS_PER_DAY);
  }

  /**
   * Get a random word for practice mode
   */
  getRandomWord(): string {
    const index = Math.floor(Math.random() * SOLUTION_WORDS.length);
    return SOLUTION_WORDS[index];
  }

  /**
   * Add a letter to the current guess
   */
  addLetter(state: WordleGameState, letter: string): WordleGameState {
    if (state.gameStatus !== 'playing') return state;
    if (state.currentGuess.length >= WORD_LENGTH) return state;

    return {
      ...state,
      currentGuess: state.currentGuess + letter.toUpperCase(),
    };
  }

  /**
   * Remove the last letter from the current guess
   */
  removeLetter(state: WordleGameState): WordleGameState {
    if (state.gameStatus !== 'playing') return state;
    if (state.currentGuess.length === 0) return state;

    return {
      ...state,
      currentGuess: state.currentGuess.slice(0, -1),
    };
  }

  /**
   * Submit the current guess
   * Returns [newState, errorMessage]
   */
  submitGuess(state: WordleGameState): [WordleGameState, string | null] {
    if (state.gameStatus !== 'playing') {
      return [state, 'Game is already over'];
    }

    if (state.currentGuess.length !== WORD_LENGTH) {
      return [state, 'Not enough letters'];
    }

    if (!isValidWord(state.currentGuess)) {
      return [state, 'Not in word list'];
    }

    // Hard mode validation
    if (state.hardMode) {
      const hardModeError = this.validateHardMode(state);
      if (hardModeError) {
        return [state, hardModeError];
      }
    }

    // Evaluate the guess
    const guessResult = this.evaluateGuess(state.currentGuess, state.targetWord);

    // Update keyboard state
    const newKeyboardState = this.updateKeyboardState(
      state.keyboardState,
      guessResult
    );

    // Determine new game status
    const isCorrect = guessResult.isCorrect;
    const isLastGuess = state.currentRow >= MAX_GUESSES - 1;
    let newStatus: WordleGameStatus = 'playing';

    if (isCorrect) {
      newStatus = 'won';
    } else if (isLastGuess) {
      newStatus = 'lost';
    }

    const newState: WordleGameState = {
      ...state,
      guesses: [...state.guesses, guessResult],
      currentGuess: '',
      currentRow: state.currentRow + 1,
      gameStatus: newStatus,
      keyboardState: newKeyboardState,
      endedAt: newStatus !== 'playing' ? Date.now() : undefined,
    };

    return [newState, null];
  }

  /**
   * Evaluate a guess against the target word.
   * Handles duplicate letters correctly.
   */
  evaluateGuess(guess: string, target: string): GuessResult {
    const guessUpper = guess.toUpperCase();
    const targetUpper = target.toUpperCase();
    const letters: LetterResult[] = Array(WORD_LENGTH).fill(null).map((_, i) => ({
      letter: guessUpper[i],
      state: 'absent' as LetterState,
    }));

    // Track which target letters have been matched
    const targetLetterCounts: Record<string, number> = {};
    for (const char of targetUpper) {
      targetLetterCounts[char] = (targetLetterCounts[char] || 0) + 1;
    }

    // First pass: mark correct positions (green)
    for (let i = 0; i < WORD_LENGTH; i++) {
      if (guessUpper[i] === targetUpper[i]) {
        letters[i].state = 'correct';
        targetLetterCounts[guessUpper[i]]--;
      }
    }

    // Second pass: mark present letters (yellow)
    for (let i = 0; i < WORD_LENGTH; i++) {
      if (letters[i].state === 'correct') continue;
      const char = guessUpper[i];
      if (targetLetterCounts[char] && targetLetterCounts[char] > 0) {
        letters[i].state = 'present';
        targetLetterCounts[char]--;
      }
    }

    const isCorrect = guessUpper === targetUpper;

    return {
      letters,
      isValid: true,
      isCorrect,
    };
  }

  /**
   * Validate hard mode constraints:
   * - Must use all previously revealed correct letters in the same position
   * - Must use all previously revealed present letters somewhere
   */
  validateHardMode(state: WordleGameState): string | null {
    if (state.guesses.length === 0) return null;

    const currentGuess = state.currentGuess.toUpperCase();

    for (const prevGuess of state.guesses) {
      for (let i = 0; i < prevGuess.letters.length; i++) {
        const { letter, state: letterState } = prevGuess.letters[i];

        if (letterState === 'correct') {
          // Must have this letter in the same position
          if (currentGuess[i] !== letter) {
            return `${this.ordinal(i + 1)} letter must be ${letter}`;
          }
        }

        if (letterState === 'present') {
          // Must contain this letter somewhere
          if (!currentGuess.includes(letter)) {
            return `Guess must contain ${letter}`;
          }
        }
      }
    }

    return null;
  }

  /**
   * Update the keyboard state with new guess results.
   * Priority: correct > present > absent
   */
  updateKeyboardState(
    currentState: KeyboardState,
    guessResult: GuessResult
  ): KeyboardState {
    const newState = { ...currentState };

    for (const { letter, state } of guessResult.letters) {
      const existing = newState[letter];

      // correct always wins
      if (state === 'correct') {
        newState[letter] = 'correct';
      } else if (state === 'present' && existing !== 'correct') {
        newState[letter] = 'present';
      } else if (state === 'absent' && !existing) {
        newState[letter] = 'absent';
      }
    }

    return newState;
  }

  /**
   * Generate share text (emoji grid) for completed games
   */
  generateShareText(state: WordleGameState): string {
    if (state.gameStatus === 'playing') return '';

    const puzzleLabel = state.mode === 'daily'
      ? `Daily Word #${state.puzzleNumber}`
      : 'Daily Word (Practice)';

    const guessCount = state.gameStatus === 'won'
      ? `${state.guesses.length}/${MAX_GUESSES}`
      : `X/${MAX_GUESSES}`;

    const hardModeLabel = state.hardMode ? '*' : '';

    const emojiGrid = state.guesses.map(guess =>
      guess.letters.map(({ state: s }) => {
        switch (s) {
          case 'correct': return '\u{1F7E9}'; // green square
          case 'present': return '\u{1F7E8}'; // yellow square
          default: return '\u2B1B'; // black square
        }
      }).join('')
    ).join('\n');

    return `${puzzleLabel} ${guessCount}${hardModeLabel}\n\n${emojiGrid}`;
  }

  /**
   * Get milliseconds until midnight (next daily word)
   */
  getTimeUntilNextWord(): number {
    const now = new Date();
    const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    return tomorrow.getTime() - now.getTime();
  }

  /**
   * Check if the daily puzzle has already been completed today
   */
  isDailyCompleted(lastCompletedPuzzle?: number): boolean {
    if (lastCompletedPuzzle === undefined) return false;
    return lastCompletedPuzzle >= this.getDailyPuzzleNumber();
  }

  private generateGameId(): string {
    return `wordle_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private ordinal(n: number): string {
    const suffixes = ['th', 'st', 'nd', 'rd'];
    const mod = n % 100;
    return n + (suffixes[(mod - 20) % 10] || suffixes[mod] || suffixes[0]);
  }
}
